import React, { useState, useEffect } from "react";
import {
    Plus,
    Mic,
    Clock,
    CheckCircle2,
    AlertCircle,
    Loader2,
    Upload,
    FileText,
    Trash2,
    Users,
    Search,
    RotateCw,
    Edit,
    ChevronRight,
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { toast } from "sonner";
import { useMeeting, useMeetingRecording } from "../../../../context/MeetingContext";
import { getMeetingSessionById } from "../../../../service/apiService";
import { MeetingSession, RecordingStatus } from "../../../../types";
import MeetingRecorder from "./MeetingRecorder";
import SpeakerMapping from "./SpeakerMapping";
import TranscriptionViewer from "./TranscriptionViewer";
import MeetingDetailView from "./MeetingDetailView";

type ViewMode = 'list' | 'recording' | 'detail' | 'speaker-mapping' | 'transcription';

interface MeetingManagementProps {
    /** Closes the Meetings dialog -- passed down so the detail view can leave
        for the chat when a Virtual Assistant is chosen. */
    onClose?: () => void;
}

const MeetingManagement: React.FC<MeetingManagementProps> = ({ onClose }) => {
    const {
        sessions,
        selectedSession,
        loading,
        pagination,
        fetchSessions,
        setSelectedSession,
        deleteSession,
        updateSession,
        refreshSession,
        retryTranscription,
    } = useMeeting();

    // A recording survives this modal being closed (it lives in
    // MeetingContext at the app root), so reopening must land back on
    // the recorder rather than the list — otherwise the running meeting
    // looks lost and the user starts a second one.
    const { recordingState } = useMeetingRecording();
    const [viewMode, setViewMode] = useState<ViewMode>(
        () => (recordingState === 'idle' ? 'list' : 'recording')
    );
    const [searchQuery, setSearchQuery] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState<MeetingSession | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [sessionToEdit, setSessionToEdit] = useState<MeetingSession | null>(null);
    const [editTitle, setEditTitle] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [refreshingSessionId, setRefreshingSessionId] = useState<string | null>(null);
    const [retryingSessionId, setRetryingSessionId] = useState<string | null>(null);

    // Check if a session has been stuck in processing/transcribing for more than 3 hours
    const isStuckProcessing = (session: MeetingSession): boolean => {
        if (session.recording_status === 'failed') return true;
        if (session.recording_status !== 'processing' && session.recording_status !== 'transcribing') return false;
        const statusTime = new Date(session.status_changed_at || session.updated_at).getTime();
        const threeHoursMs = 3 * 60 * 60 * 1000;
        return (Date.now() - statusTime) > threeHoursMs;
    };

    // Initial fetch (the provider deliberately does not fetch on mount)
    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    // Poll the list only while it is actually visible: the list view is
    // showing, the tab is in the foreground, and something is still
    // processing. The detail view runs its own status poll.
    const hasActiveSessions = sessions.some(s =>
        s.recording_status === 'processing' ||
        s.recording_status === 'transcribing'
    );

    useEffect(() => {
        if (!hasActiveSessions || viewMode !== 'list') return;

        const interval = setInterval(() => {
            if (!document.hidden) {
                fetchSessions();
            }
        }, 30000); // 30 seconds polling for list view

        return () => clearInterval(interval);
    }, [fetchSessions, hasActiveSessions, viewMode]);

    // Filter sessions by search query
    const filteredSessions = React.useMemo(() => {
        if (!searchQuery.trim()) return sessions;

        const query = searchQuery.toLowerCase();
        return sessions.filter((session) => {
            const title = session.title?.toLowerCase() || '';
            const description = session.description?.toLowerCase() || '';
            return title.includes(query) || description.includes(query);
        });
    }, [sessions, searchQuery]);

    const handleNewRecording = () => {
        setViewMode('recording');
    };

    const handleViewSession = (session: MeetingSession) => {
        setSelectedSession(session);
        setViewMode('detail');
    };

    const handleDeleteClick = (session: MeetingSession, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setSessionToDelete(session);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!sessionToDelete?.id) return;

        setIsDeleting(true);
        try {
            await deleteSession(sessionToDelete.id);
            setShowDeleteModal(false);
            setSessionToDelete(null);
        } catch (err) {
            // Context already toasted; keep the modal open so the user
            // can retry or cancel — closing it would read as success.
            console.error("Error deleting session:", err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleEditClick = (session: MeetingSession, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setSessionToEdit(session);
        setEditTitle(session.title || '');
        setEditDescription(session.description || '');
        setShowEditModal(true);
    };

    const handleSaveEdit = async () => {
        if (!sessionToEdit?.id) return;

        setIsSaving(true);
        try {
            await updateSession(sessionToEdit.id, {
                title: editTitle,
                description: editDescription
            });
            setShowEditModal(false);
            setSessionToEdit(null);
            toast.success("Meeting updated successfully");
        } catch (err) {
            console.error("Error updating session:", err);
            toast.error("Failed to update meeting");
        } finally {
            setIsSaving(false);
        }
    };

    const handleViewTranscription = (session: MeetingSession, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setSelectedSession(session);
        setViewMode('transcription');
    };

    const handleViewSpeakerMapping = (session: MeetingSession, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setSelectedSession(session);
        setViewMode('speaker-mapping');
    };

    const handleRecordingComplete = async (sessionId?: string) => {
        if (sessionId) {
            try {
                // Fetch the definitive session data to ensure we have the latest status
                const session = await getMeetingSessionById(sessionId);
                if (session) {
                    setSelectedSession(session);
                    setViewMode('detail');
                    // Background refresh of the list
                    fetchSessions();
                    return;
                }
            } catch (error) {
                console.error("Failed to load new session details:", error);
                toast.error("Could not load recording details");
            }
        }

        // Fallback to list view
        setViewMode('list');
        fetchSessions();
    };

    const handleBackToList = () => {
        setViewMode('list');
        setSelectedSession(null);
    };

    const getStatusIcon = (status: RecordingStatus) => {
        switch (status) {
            case 'recording':
                return <Mic className="w-4 h-4 text-red-500 animate-pulse" />;
            case 'uploading':
                return <Upload className="w-4 h-4 text-blue-500" />;
            case 'processing':
            case 'transcribing':
                return <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />;
            case 'transcribed':
            case 'completed':
                return <CheckCircle2 className="w-4 h-4 text-green-500" />;
            case 'failed':
                return <AlertCircle className="w-4 h-4 text-red-500" />;
            default:
                return <Clock className="w-4 h-4 text-gray-400" />;
        }
    };

    const getStatusLabel = (status: RecordingStatus) => {
        switch (status) {
            case 'recording':
                return 'Recording';
            case 'uploading':
                return 'Uploading';
            case 'processing':
                return 'Processing';
            case 'transcribing':
                return 'Transcribing';
            case 'transcribed':
                return 'Transcribed';
            case 'completed':
                return 'Completed';
            case 'failed':
                return 'Failed';
            default:
                return status;
        }
    };

    const formatDuration = (seconds?: number) => {
        if (!seconds) return '--:--';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    // Render based on view mode
    if (viewMode === 'recording') {
        return (
            <MeetingRecorder
                onComplete={handleRecordingComplete}
                onCancel={handleBackToList}
            />
        );
    }

    if (viewMode === 'speaker-mapping' && selectedSession) {
        return (
            <SpeakerMapping
                session={selectedSession}
                onBack={handleBackToList}
                onComplete={() => {
                    refreshSession(selectedSession.id);
                    handleBackToList();
                }}
            />
        );
    }

    if (viewMode === 'transcription' && selectedSession) {
        return (
            <TranscriptionViewer
                session={selectedSession}
                onBack={handleBackToList}
                onEditSpeakers={() => handleViewSpeakerMapping(selectedSession)}
            />
        );
    }

    if (viewMode === 'detail' && selectedSession) {
        return (
            <MeetingDetailView
                session={selectedSession}
                onBack={handleBackToList}
                onEditSpeakers={() => handleViewSpeakerMapping(selectedSession)}
                onClose={onClose}
            />
        );
    }


    // Row actions are identical in the desktop table and the mobile card
    // list, so they live in one place — same handlers, same order.
    //
    // `compact` (the mobile cards) keeps every action but shows a text label
    // beside each icon. A bare icon row read as "open this meeting", so people
    // tapped the document icon and landed on the cut-down transcription view;
    // labels plus the card's own chevron make the two paths unambiguous.
    const renderRowActions = (session: MeetingSession, compact = false) => {
        const actionClass = (tone: string) =>
            compact
                ? `flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${tone}`
                : `p-1.5 rounded focus:outline-none ${tone}`;

        return (
        <div className={compact
            ? "flex flex-wrap items-center gap-1.5"
            : "flex items-center justify-end space-x-1"}>
            {session.recording_status === 'completed' && (
                <>
                    <button
                        onClick={(e) => handleViewTranscription(session, e)}
                        className={actionClass("text-violet-600 hover:text-violet-800 hover:bg-violet-50")}
                        title="View Transcription"
                    >
                        <FileText className="h-4 w-4" />
                        {compact && <span>Transcript</span>}
                    </button>
                    <button
                        onClick={(e) => handleViewSpeakerMapping(session, e)}
                        className={actionClass("text-blue-600 hover:text-blue-800 hover:bg-blue-50")}
                        title="Identify Speakers"
                    >
                        <Users className="h-4 w-4" />
                        {compact && <span>Speakers</span>}
                    </button>
                </>
            )}
            {(session.recording_status === 'processing' || session.recording_status === 'transcribing') && (
                <button
                    onClick={async (e) => {
                        e.stopPropagation();
                        setRefreshingSessionId(session.id);
                        try {
                            await refreshSession(session.id);
                        } finally {
                            setRefreshingSessionId(null);
                        }
                    }}
                    className={actionClass("text-gray-600 hover:text-gray-800 hover:bg-gray-50")}
                    title="Refresh Status"
                    disabled={refreshingSessionId === session.id}
                >
                    <RotateCw className={`h-4 w-4 ${refreshingSessionId === session.id ? 'animate-spin' : ''}`} />
                    {compact && <span>Refresh</span>}
                </button>
            )}
            {isStuckProcessing(session) && (
                <button
                    onClick={async (e) => {
                        e.stopPropagation();
                        setRetryingSessionId(session.id);
                        try {
                            await retryTranscription(session.id);
                        } finally {
                            setRetryingSessionId(null);
                        }
                    }}
                    className="px-2 py-1 text-xs font-medium text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded focus:outline-none disabled:opacity-50"
                    title="Retry Transcription"
                    disabled={retryingSessionId === session.id}
                >
                    {retryingSessionId === session.id ? 'Retrying...' : 'Retry'}
                </button>
            )}
            <button
                onClick={(e) => handleEditClick(session, e)}
                className={actionClass("text-gray-600 hover:text-gray-800 hover:bg-gray-50")}
                title="Edit Title"
            >
                <Edit className="h-4 w-4" />
                {compact && <span>Edit</span>}
            </button>
            <button
                onClick={(e) => handleDeleteClick(session, e)}
                className={actionClass("text-red-600 hover:text-red-800 hover:bg-red-50")}
                title="Delete"
            >
                <Trash2 className="h-4 w-4" />
                {compact && <span>Delete</span>}
            </button>
        </div>
        );
    };

    // Desktop Table view
    return (
        <>
            <div className="mx-auto w-full">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    {/* Header with search and create button */}
                    <div className="p-3 sm:p-4 border-b">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="relative w-full sm:flex-1 sm:max-w-md">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="block w-full pl-10 pr-3 py-2 bg-white text-gray-900 border border-gray-200 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    placeholder="Search Meetings"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <button
                                onClick={handleNewRecording}
                                className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center w-full sm:w-auto flex-shrink-0 sm:ml-4"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                New Recording
                            </button>
                        </div>
                    </div>

                    {/* Table — md and up. `min-w` keeps the columns at a
                        readable width and lets the wrapper actually scroll
                        instead of silently compressing every cell. */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full min-w-[720px] divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="w-1/4 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Title
                                    </th>
                                    <th scope="col" className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col" className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Duration
                                    </th>
                                    <th scope="col" className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th scope="col" className="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">
                                            <div className="flex items-center justify-center space-x-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                                                <span>Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredSessions.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">
                                            {searchQuery
                                                ? "No meetings match your search"
                                                : "No meetings yet. Click 'New Recording' to get started."}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredSessions.map((session) => (
                                        <tr
                                            key={session.id}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => handleViewSession(session)}
                                        >
                                            <td className="px-4 py-3 max-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate" title={session.title || 'Untitled Recording'}>
                                                    {session.title || 'Untitled Recording'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2 text-sm text-gray-900">
                                                    {getStatusIcon(session.recording_status)}
                                                    <span>{getStatusLabel(session.recording_status)}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm text-gray-900">
                                                    {formatDuration(session.audio_duration_seconds)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm text-gray-900">
                                                    {formatDate(session.created_at)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right text-sm font-medium">
                                                {renderRowActions(session)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Card list — below md. Same data and the same handlers
                        as the table, stacked so nothing is squeezed. */}
                    <div className="md:hidden divide-y divide-gray-200">
                        {loading ? (
                            <div className="px-4 py-6 text-center text-sm text-gray-500">
                                <div className="flex items-center justify-center space-x-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                                    <span>Loading...</span>
                                </div>
                            </div>
                        ) : filteredSessions.length === 0 ? (
                            <div className="px-4 py-6 text-center text-sm text-gray-500">
                                {searchQuery
                                    ? "No meetings match your search"
                                    : "No meetings yet. Tap 'New Recording' to get started."}
                            </div>
                        ) : (
                            filteredSessions.map((session) => (
                                <div
                                    key={session.id}
                                    className="px-3 py-3 active:bg-gray-50 cursor-pointer"
                                    onClick={() => handleViewSession(session)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleViewSession(session);
                                        }
                                    }}
                                >
                                    {/* The chevron makes the card itself the
                                        way in — it opens the full detail view. */}
                                    <div className="flex items-start gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-gray-900 break-words">
                                                {session.title || 'Untitled Recording'}
                                            </div>
                                            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                                                <span className="flex items-center gap-1.5 text-gray-900">
                                                    {getStatusIcon(session.recording_status)}
                                                    {getStatusLabel(session.recording_status)}
                                                </span>
                                                <span>{formatDuration(session.audio_duration_seconds)}</span>
                                                <span>{formatDate(session.created_at)}</span>
                                            </div>
                                        </div>
                                        <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                        {renderRowActions(session, true)}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Pagination */}
                    {pagination.total_pages > 1 && (
                        <div className="flex items-center justify-center gap-2 p-4 border-t border-gray-200">
                            <button
                                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
                                disabled={pagination.page === 1}
                                onClick={() => fetchSessions(pagination.page - 1)}
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-500">
                                Page {pagination.page} of {pagination.total_pages}
                            </span>
                            <button
                                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
                                disabled={!pagination.has_next}
                                onClick={() => fetchSessions(pagination.page + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>

                {/* Delete Confirmation Modal */}
                <Transition appear show={showDeleteModal} as={Fragment}>
                    <Dialog as="div" className="relative z-50" onClose={() => !isDeleting && setShowDeleteModal(false)}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-black bg-opacity-25" />
                        </Transition.Child>

                        <div className="fixed inset-0 overflow-y-auto">
                            <div className="flex min-h-full items-center justify-center p-4 text-center">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-300"
                                    enterFrom="opacity-0 scale-95"
                                    enterTo="opacity-100 scale-100"
                                    leave="ease-in duration-200"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                                            <Trash2 className="w-6 h-6 text-red-600" />
                                        </div>

                                        <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 text-center mb-2">
                                            Delete Meeting
                                        </Dialog.Title>

                                        <div className="mb-6 text-center">
                                            <p className="text-sm text-gray-500 mb-2">
                                                Are you sure you want to delete
                                            </p>
                                            <p className="text-sm font-medium text-gray-900 mb-3">
                                                "{sessionToDelete?.title || 'Untitled Recording'}"?
                                            </p>
                                            <p className="text-sm text-amber-600 font-medium">
                                                ⚠️ You will lose all data associated with this meeting.
                                            </p>
                                            {/* WS-02: BrainBank copies are independent documents with
                                                their own readers — deleting the session leaves them in
                                                place, and the user has to know that. */}
                                            {!!sessionToDelete?.brain_bank_exports?.length && (
                                                <p className="text-sm text-gray-500 mt-3">
                                                    {sessionToDelete.brain_bank_exports.length} transcript
                                                    cop{sessionToDelete.brain_bank_exports.length === 1 ? 'y' : 'ies'}{' '}
                                                    already sent to BrainBanks will not be deleted. Remove
                                                    {sessionToDelete.brain_bank_exports.length === 1 ? ' it' : ' them'} from the
                                                    session first if you want {sessionToDelete.brain_bank_exports.length === 1 ? 'it' : 'them'} gone too.
                                                </p>
                                            )}
                                        </div>

                                        <div className="flex space-x-3 justify-center">
                                            <button
                                                type="button"
                                                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none disabled:opacity-50"
                                                onClick={() => {
                                                    setShowDeleteModal(false);
                                                    setSessionToDelete(null);
                                                }}
                                                disabled={isDeleting}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none disabled:opacity-50"
                                                onClick={handleDelete}
                                                disabled={isDeleting}
                                            >
                                                {isDeleting ? (
                                                    <div className="flex items-center">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                        Deleting...
                                                    </div>
                                                ) : (
                                                    'Delete'
                                                )}
                                            </button>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition>

                {/* Edit Meeting Modal */}
                <Transition appear show={showEditModal} as={Fragment}>
                    <Dialog as="div" className="relative z-50" onClose={() => !isSaving && setShowEditModal(false)}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-black bg-opacity-25" />
                        </Transition.Child>

                        <div className="fixed inset-0 overflow-y-auto">
                            <div className="flex min-h-full items-center justify-center p-4 text-center">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-300"
                                    enterFrom="opacity-0 scale-95"
                                    enterTo="opacity-100 scale-100"
                                    leave="ease-in duration-200"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                        <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                                            Edit Meeting
                                        </Dialog.Title>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Title
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editTitle}
                                                    onChange={(e) => setEditTitle(e.target.value)}
                                                    className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                                    placeholder="Meeting title..."
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Description (optional)
                                                </label>
                                                <textarea
                                                    value={editDescription}
                                                    onChange={(e) => setEditDescription(e.target.value)}
                                                    rows={3}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                                    placeholder="Add notes about this meeting..."
                                                />
                                            </div>
                                        </div>

                                        <div className="flex space-x-3 justify-end mt-6">
                                            <button
                                                type="button"
                                                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none disabled:opacity-50"
                                                onClick={() => {
                                                    setShowEditModal(false);
                                                    setSessionToEdit(null);
                                                }}
                                                disabled={isSaving}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="inline-flex justify-center rounded-md border border-transparent bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none disabled:opacity-50"
                                                onClick={handleSaveEdit}
                                                disabled={isSaving || !editTitle.trim()}
                                            >
                                                {isSaving ? (
                                                    <div className="flex items-center">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                        Saving...
                                                    </div>
                                                ) : (
                                                    'Save'
                                                )}
                                            </button>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition>
            </div>
        </>
    );
};

export default MeetingManagement;
